// E14 (DecisionLog §4.14): the five fixed banner positions on the browse
// home. Enums arrive as integers (see the API's PromotionSlotType).
export enum PromotionSlotType {
    TopHero = 1,
    MidPage = 2,
    Tile1 = 3,
    Tile2 = 4,
    Tile3 = 5,
}

// The admin page's name for each slot, in page order.
export const PROMOTION_SLOT_LABELS: Record<PromotionSlotType, string> = {
    [PromotionSlotType.TopHero]: "Top hero",
    [PromotionSlotType.MidPage]: "Mid page",
    [PromotionSlotType.Tile1]: "Tile 1",
    [PromotionSlotType.Tile2]: "Tile 2",
    [PromotionSlotType.Tile3]: "Tile 3",
};

// One live promotion, as GET /promotions/home returns it.
export interface HomePromotion {
    id: string;
    slot: PromotionSlotType;
    label: string;
    ctaLabel: string | null;
    // An app path starting with a single "/" — the API validates it on write
    // (PO ruling 2026-10-05: no outside links for now).
    ctaHref: string | null;
    imageUrl: string | null;
    // Set on a vendor promotion; with no ctaHref it links to that vendor's
    // page, /c/v/<vendorSlug> (#425), or to its grid while an API that sends
    // no slug is still deployed (toPromoBannerProps).
    vendorId: number | null;
    // The vendor's page slug (#425): set on a vendor promotion, null on a
    // house promotion.
    vendorSlug: string | null;
}

// One row of GET /promotions (the admin list), inactive (deactivated) rows
// included. Dates are UTC; the live window is half-open, [startsAt, endsAt).
export interface Promotion {
    id: string;
    slot: PromotionSlotType;
    label: string;
    ctaLabel: string | null;
    ctaHref: string | null;
    imageUrl: string | null;
    startsAt: string | null;
    endsAt: string | null;
    isActive: boolean;
    vendorId: number | null;
    createdAt: string;
    // Null for a row never updated since it was created.
    updatedAt: string | null;
}

// The fields POST /promotions and PUT /promotions/{id} share. Sent as
// multipart `Body.*` parts; a null field is left out of the form, which the
// update reads as "clear it". `image` null sends no file: the create stores
// none, the update keeps the current one.
export interface PromotionRequest {
    slot: PromotionSlotType;
    label: string;
    ctaLabel: string | null;
    ctaHref: string | null;
    startsAt: string | null;
    endsAt: string | null;
    vendorId: number | null;
    image: File | null;
}

// The update also states IsActive, every time: the API refuses an update
// without it rather than reading an omission as a deactivation.
export interface UpdatePromotionRequest extends PromotionRequest {
    isActive: boolean;
}

export interface CreatePromotionResponse {
    id: string;
}

// A slot with nothing live is null, never an empty object. `tilesRow` always
// has exactly three entries — Tile1, Tile2, Tile3 — so a null keeps its
// position.
export interface GetHomePromotionsResponse {
    topHero: HomePromotion | null;
    midPagePromo: HomePromotion | null;
    tilesRow: (HomePromotion | null)[];
}
